import type { Meeting } from '../lib/meetingApi'
import {
  AGENDA,
  agendaItem,
  elapsedMs,
  formatClock,
  sectionRemainingMs,
  TOTAL_MINUTES,
} from '../lib/meeting'

interface Props {
  meeting: Meeting
  /** Server-corrected "now" — the caller has already subtracted its skew. */
  nowMs: number
  isAdmin: boolean
  busy: boolean
  /** Admin only: jump to a section (Back / Next / tapping a step). */
  onSection: (section: number) => void
}

/**
 * The sticky strip at the top of a running meeting: which section, how long
 * is left in it, how far through the hour, the six steps, and — for the
 * presenter — Back / Next.
 *
 * Every number here is derived from timestamps the server set plus its own
 * now(), so a phone whose clock is wrong still shows the presenter's clock.
 */
export default function MeetingBar({ meeting, nowMs, isAdmin, busy, onSection }: Props) {
  const section = meeting.current_section
  const item = agendaItem(section)
  const remaining = sectionRemainingMs(meeting, nowMs)
  const elapsed = elapsedMs(meeting, nowMs)
  const over = remaining < 0

  return (
    <div className="meeting-bar" role="region" aria-label="Meeting">
      <div className="meeting-bar__top">
        <div className="meeting-bar__what">
          <div className="meeting-bar__section">{item.label}</div>
          <div className="meeting-bar__total">
            {formatClock(elapsed)} / {TOTAL_MINUTES}:00
          </div>
        </div>
        <div
          className={'meeting-bar__clock' + (over ? ' meeting-bar__clock--over' : '')}
          aria-live="off"
          aria-label={over ? 'Over time' : 'Time left in this section'}
        >
          {formatClock(remaining)}
        </div>
      </div>

      <ol className="meeting-steps" aria-label="Agenda">
        {AGENDA.map((s, i) => {
          const cls =
            'meeting-step' +
            (i === section ? ' meeting-step--on' : i < section ? ' meeting-step--done' : '')
          const inner = (
            <>
              <span className="meeting-step__n">{i + 1}</span>
              <span className="meeting-step__label">{s.label}</span>
            </>
          )
          return (
            <li key={s.key} className={cls} aria-current={i === section ? 'step' : undefined}>
              {isAdmin ? (
                <button
                  type="button"
                  className="meeting-step__btn"
                  disabled={busy || i === section}
                  onClick={() => onSection(i)}
                >
                  {inner}
                </button>
              ) : (
                <span className="meeting-step__btn">{inner}</span>
              )}
            </li>
          )
        })}
      </ol>

      {isAdmin && (
        <div className="meeting-nav">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy || section === 0}
            onClick={() => onSection(section - 1)}
          >
            ← Back
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || section === AGENDA.length - 1}
            onClick={() => onSection(section + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  )
}
